// Signing section — how the install's E-IMZO-signed documents are stamped
// and verified (`/api/v1/settings/signing`). The header line an organisation prints on its signed PDFs is
// its own ("IJRO.GOV.UZ tizimi orqali ERI bilan tasdiqlangan" on a
// ministry order, a Chamber's name on an audit paper), so it is authored
// here per language with {project} (the install's name, Settings) / {code}
// placeholders, together with the default visibility a newly enrolled
// document's verification page gets.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, QrCode, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFieldErrors } from '@/shared/hooks/useFieldErrors.js';
import { FieldError } from '@/shared/ui/FieldError.jsx';
import { api, ep } from '../lib/api.js';
import { STAMP_LANGS as LANGS, STAMP_TEXT_MAX, stampProblems } from '../lib/signingRules.js';

const VISIBILITIES = ['restricted', 'public', 'off'];

export const SigningTab = ({ organizationName }) => {
  const { t } = useTranslation('console');
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const checks = useFieldErrors(stampProblems(draft?.stamp_text));

  const load = async () => {
    setLoading(true);
    const res = await api.get(ep.signing());
    if (res.success) {
      setSettings(res.response);
      setDraft({
        stamp_text: { ...res.response.stamp_text },
        stamp_lang: res.response.stamp_lang,
        default_visibility: res.response.default_visibility,
      });
    } else {
      toast.error(res.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!checks.attempt()) return;
    setSaving(true);
    const stampText = Object.fromEntries(Object.entries(draft.stamp_text).map(([lang, text]) => [lang, (text || '').trim()]));
    const res = await api.put(ep.signing(), { data: { ...draft, stamp_text: stampText } });
    setSaving(false);
    if (res.success) {
      setSettings(res.response);
      toast.success(t('signing.saved'));
    } else {
      checks.showServer(res, ['stamp_text', 'stamp_lang', 'default_visibility']);
    }
  };

  if (loading || !draft) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const preview = (draft.stamp_text[draft.stamp_lang] || '')
    .replaceAll('{project}', organizationName || '…')
    .replaceAll('{code}', '3F9KQ7W2XZ8M');

  return (
    <div className="space-y-8 max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <QrCode className="w-5 h-5 text-brand" />
          {t('signing.title')}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">{t('signing.tagline')}</p>
      </div>

      <section className="space-y-3">
        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('signing.stampText')}</Label>
        <p className="text-xs text-muted-foreground">{t('signing.stampHint')}</p>
        {LANGS.map((lang) => (
          <div key={lang} className="grid grid-cols-[3rem_1fr] items-center gap-3">
            <span className="text-xs font-mono uppercase text-muted-foreground">{lang}</span>
            <div className="space-y-1">
              <Input
                value={draft.stamp_text[lang] || ''}
                maxLength={STAMP_TEXT_MAX}
                aria-label={t('signing.stampText') + ` (${lang})`}
                {...checks.describe(`stamp_text.${lang}`, 'signing')}
                onChange={(e) => {
                  setDraft({ ...draft, stamp_text: { ...draft.stamp_text, [lang]: e.target.value } });
                  checks.touch(`stamp_text.${lang}`);
                }}
              />
              <FieldError id={`signing-stamp_text.${lang}-error`} message={checks.errors[`stamp_text.${lang}`]} />
            </div>
          </div>
        ))}
        <div className="rounded-md border border-border bg-muted/30 p-3 text-xs">
          <div className="text-muted-foreground mb-1">{t('signing.preview')}</div>
          <div className="font-medium text-[#C00000]">{preview}</div>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('signing.stampLang')}</Label>
          <Select value={draft.stamp_lang} onValueChange={(v) => setDraft({ ...draft, stamp_lang: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {LANGS.map((l) => <SelectItem key={l} value={l}>{t(`signing.langs.${l}`)}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">{t('signing.stampLangHint')}</p>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('signing.defaultVisibility')}</Label>
          <Select value={draft.default_visibility} onValueChange={(v) => setDraft({ ...draft, default_visibility: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {VISIBILITIES.map((v) => <SelectItem key={v} value={v}>{t(`signing.visibility.${v}`)}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">{t('signing.defaultVisibilityHint')}</p>
        </div>
      </section>

      <section className="space-y-1">
        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('signing.verifyBase')}</Label>
        <div className="font-mono text-sm">{settings?.public_verify_base_url}/v/&lt;code&gt;</div>
        <p className="text-xs text-muted-foreground">{t('signing.verifyBaseHint')}</p>
      </section>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving || checks.blocked} data-testid="signing-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {t('signing.save')}
        </Button>
      </div>
    </div>
  );
};

export default SigningTab;
